import { getItemAttributeValueByIdentifier } from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/AttributeSelectors';
import { AttributeIdentifiers, OptionClassificationIdentifiers } from '../Enums';
import { filterColorOptions, getSelectedMaterialThickness } from './colorOptionFilter';
import { formatHexColor } from './editorUnits';
import { sourceUnitsToMm } from './editorItemLayout';
import { resolveTextColorOptionIdFromHex } from './colorOptionResolve';
import type {
  ColorSizeMetrics,
  EditorItem,
  ImageItem,
  MeasurementsByColor,
  TextItem,
} from '../Reducers/Configurator/types';

export type { ColorSizeMetrics, MeasurementsByColor };

export type ColorOption = {
  identifier?: string;
};

/** Map normalized hex values to color option identifiers. */
export function buildHexToColorOptionId(options: ColorOption[]): Record<string, string> {
  const map: Record<string, string> = {};

  options.forEach((option) => {
    if (!option.identifier) return;
    const hex = formatHexColor(getItemAttributeValueByIdentifier(option, AttributeIdentifiers.hex, ''));
    if (hex) {
      map[hex] = option.identifier;
    }
  });

  return map;
}

export function getColorOptionsFromConfiguratorState(state: {
  optionsList?: Record<string, unknown[]>;
}): ColorOption[] {
  const options = state.optionsList?.[OptionClassificationIdentifiers.color];
  return Array.isArray(options) ? (options as ColorOption[]) : [];
}

/** Color options eligible for SVG auto-mapping — restricted to the selected material thickness. */
export function getColorMappingOptionsFromConfiguratorState(state: {
  optionsList?: Record<string, unknown[]>;
  configuration?: { optionclassifications?: unknown };
}): ColorOption[] {
  const all = getColorOptionsFromConfiguratorState(state);
  const thickness = getSelectedMaterialThickness(state);
  if (!thickness) return all;
  return filterColorOptions(all, { thickness }) as ColorOption[];
}

/** Bounding-box area in square meters (width/height are millimeters). */
export function computeSquareMeter(widthMm: number, heightMm: number): number {
  if (!(widthMm > 0) || !(heightMm > 0)) return 0;
  return (widthMm / 1000) * (heightMm / 1000);
}

/** Perimeter in meters from an item's source-unit perimeter (0 when unavailable). */
function computePerimeterMeters(item: EditorItem): number {
  if (item.sourcePerimeter == null || !(item.sourcePerimeter > 0)) return 0;
  const mm = sourceUnitsToMm(item, item.sourcePerimeter) ?? 0;
  return mm / 1000;
}

/** Exact and billing (rounded) cut length in meters for the item's current display size. */
function derivePerimeterMeters(item: EditorItem): { measuredPerimeter: number; perimeter: number } {
  const measuredPerimeter = computePerimeterMeters(item);
  const perimeter = measuredPerimeter > 0 ? Math.round(measuredPerimeter) : 0;
  return { measuredPerimeter, perimeter };
}

function boundsToSquareMeter(bounds: { width: number; height: number }, item: ImageItem): number {
  if (!(bounds.width > 0) || !(bounds.height > 0)) return 0;
  if (!(item.viewBoxWidth > 0) || !(item.viewBoxHeight > 0)) return 0;

  const widthMm = (bounds.width / item.viewBoxWidth) * item.width;
  const heightMm = (bounds.height / item.viewBoxHeight) * item.height;
  return computeSquareMeter(widthMm, heightMm);
}

/** Scale per-color box areas so their sum matches the item's display area. */
function normalizeColorSquareMeters(rawAreas: number[], totalSquareMeter: number): number[] {
  if (!(totalSquareMeter > 0) || rawAreas.length === 0) {
    return rawAreas.map(() => 0);
  }

  const sumRaw = rawAreas.reduce((total, area) => total + area, 0);
  if (!(sumRaw > 0)) {
    const equalShare = totalSquareMeter / rawAreas.length;
    return rawAreas.map(() => equalShare);
  }

  return rawAreas.map((area) => (area / sumRaw) * totalSquareMeter);
}

function addImageMeasurementsByColor(
  image: ImageItem,
  addMetrics: (optionId: string, squareMeter: number, measuredPerimeter: number) => void
): void {
  if (!image.colorAreas?.length) return;

  const totalSquareMeter = image.squareMeter ?? computeSquareMeter(image.width, image.height);
  const totalMeasuredPerimeter = image.measuredPerimeter ?? computePerimeterMeters(image);
  const hasBounds = image.colorAreaBounds?.some((bounds) => bounds.width > 0 && bounds.height > 0) ?? false;

  const rawAreas = image.colorAreas.map((area, index) => {
    if (!hasBounds) {
      return totalSquareMeter / image.colorAreas!.length;
    }
    const bounds = image.colorAreaBounds?.[index];
    if (!bounds) return 0;
    return boundsToSquareMeter(bounds, image);
  });

  const normalizedAreas = hasBounds ? normalizeColorSquareMeters(rawAreas, totalSquareMeter) : rawAreas;

  image.colorAreas.forEach((area, index) => {
    addMetrics(area.colorOptionId, normalizedAreas[index] ?? 0, index === 0 ? totalMeasuredPerimeter : 0);
  });
}

function resolveTextColorOptionId(item: TextItem, hexToOptionId: Record<string, string>): string | null {
  return resolveTextColorOptionIdFromHex(item.color, item.colorOptionId, hexToOptionId);
}

export function enrichEditorItemMetrics(
  item: EditorItem,
  hexToOptionId: Record<string, string> = {}
): EditorItem {
  const { measuredPerimeter, perimeter } = derivePerimeterMeters(item);

  if (item.type === 'text') {
    const colorOptionId = resolveTextColorOptionId(item, hexToOptionId);

    return {
      ...item,
      ...(colorOptionId ? { colorOptionId } : {}),
      squareMeter: computeSquareMeter(item.width, item.height),
      measuredPerimeter,
      perimeter,
    };
  }

  return {
    ...item,
    squareMeter: computeSquareMeter(item.width, item.height),
    measuredPerimeter,
    perimeter,
  };
}

export function computeMeasurementsByColor(
  objects: Record<string, EditorItem>,
  hexToOptionId: Record<string, string> = {}
): MeasurementsByColor {
  const byOption: MeasurementsByColor = {};

  const addMetrics = (optionId: string, squareMeter: number, measuredPerimeter: number) => {
    if (!optionId) return;
    if (!byOption[optionId]) {
      byOption[optionId] = { squareMeter: 0, perimeter: 0, measuredPerimeter: 0 };
    }
    byOption[optionId].squareMeter += squareMeter;
    byOption[optionId].measuredPerimeter += measuredPerimeter;
    if (measuredPerimeter > 0) {
      byOption[optionId].perimeter += Math.round(measuredPerimeter);
    }
  };

  Object.values(objects).forEach((item) => {
    if (item.type === 'text') {
      const optionId = resolveTextColorOptionId(item, hexToOptionId);
      if (!optionId) return;
      addMetrics(
        optionId,
        item.squareMeter ?? computeSquareMeter(item.width, item.height),
        item.measuredPerimeter ?? computePerimeterMeters(item)
      );
      return;
    }

    const image = item as ImageItem;
    addImageMeasurementsByColor(image, addMetrics);
  });

  return byOption;
}
